// client/src/components/pages/LexiStar/Commissioning/commissioningLogic.js
//
// Pure helpers for commissioning — the parallel run, the accuracy audit and
// the Trip Reconciliation Certificate (T4-S15; LS-09 §5.20; m3-contracts §3.1
// parallel_runs / parallel_run_records / certificates, §7.1 report shape):
// the five steps, the five accuracy metrics and their floors, the floor gate
// that blocks sign-off, template / manual-entry / error-report CSVs, and the
// daily comparison series.

import { fmtNum } from '../StatusBoard/format';

export const STEPS = [
  { id: 'scope', label: 'Scope', help: 'Window, trucks and lanes' },
  { id: 'records', label: 'Their records', help: 'Trip sheets, tickets, fuel issues, odometers' },
  { id: 'daily', label: 'Daily comparison', help: 'Watch it build day by day' },
  { id: 'report', label: 'Accuracy report', help: 'Per truck and lane against each floor' },
  { id: 'signoff', label: 'Sign-off', help: 'Issue the certificate' },
];

export const RECORD_KINDS = [
  {
    value: 'trip_sheet',
    label: 'Trip sheets',
    help: 'Their own trip log: truck, start, route or lane.',
  },
  { value: 'ticket', label: 'Weighbridge tickets', help: 'Ticket numbers with truck and time.' },
  { value: 'fuel_issue', label: 'Fuel issues', help: 'Diesel issued: truck, time, litres.' },
  { value: 'odometer', label: 'Odometer readings', help: 'Truck, date and reading or distance.' },
];

export const DEFAULT_FLOORS = {
  trips_detected: 0.98,
  distance_pct: 0.02,
  fuel_measured_pct: 0.03,
  fuel_estimated_pct: 0.08,
  tickets_matched: 0.95,
};

/**
 * The five metrics. `kind: 'rate'` passes at or above the floor (share found);
 * `kind: 'error'` passes at or below it (mean absolute percentage error).
 */
export const METRICS = [
  {
    key: 'trips_detected',
    label: 'Trips detected',
    kind: 'rate',
    what: 'of their trips found by LexiStar',
  },
  {
    key: 'distance_pct',
    label: 'Distance',
    kind: 'error',
    what: 'mean difference from their distance',
  },
  {
    key: 'fuel_measured_pct',
    label: 'Fuel — measured',
    kind: 'error',
    what: 'mean difference from fuel issued (CAN / probe)',
  },
  {
    key: 'fuel_estimated_pct',
    label: 'Fuel — estimated',
    kind: 'error',
    what: 'mean difference from fuel issued (estimated)',
  },
  {
    key: 'tickets_matched',
    label: 'Tickets matched',
    kind: 'rate',
    what: 'of their tickets matched to a trip',
  },
];

const pct = (v, digits = 1) =>
  v === null || v === undefined || !Number.isFinite(Number(v))
    ? '—'
    : `${(Number(v) * 100).toFixed(digits)}%`;

/** The measured value of a metric (rate or mean abs pct), or null. */
export function metricValue(m, kind) {
  if (!m) return null;
  const v = kind === 'rate' ? m.rate : m.mean_abs_pct;
  return v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v);
}

/** Sample size behind a metric (0 → not assessed). */
export function metricSample(m, key) {
  if (!m) return 0;
  if (key === 'trips_detected') return Number(m.theirs ?? (m.matched ?? 0) + (m.missed ?? 0)) || 0;
  if (key === 'tickets_matched') return Number(m.tickets ?? 0) || 0;
  return Number(m.pairs ?? 0) || 0;
}

/** true / false / null (not assessed) — the server's `pass` wins when present. */
export function metricPass(m, meta, floors = DEFAULT_FLOORS) {
  if (!m) return null;
  if (typeof m.pass === 'boolean') return m.pass;
  const v = metricValue(m, meta.kind);
  if (v === null || metricSample(m, meta.key) === 0) return null;
  const floor = Number(m.floor ?? floors[meta.key]);
  return meta.kind === 'rate' ? v >= floor : v <= floor;
}

/** "98.6% · 142 of 144" / "1.4% over 131 pairs". */
export function metricText(m, meta) {
  if (!m) return 'Not assessed';
  const v = metricValue(m, meta.kind);
  const n = metricSample(m, meta.key);
  if (v === null || n === 0) return 'Not assessed';
  if (meta.key === 'trips_detected') return `${pct(v)} · ${fmtNum(m.matched)} of ${fmtNum(n)}`;
  if (meta.key === 'tickets_matched')
    return `${pct(v)} · ${fmtNum((m.auto ?? 0) + (m.manual ?? 0))} of ${fmtNum(n)}`;
  return `${pct(v)} over ${fmtNum(n)} pair${n === 1 ? '' : 's'}`;
}

export function floorText(meta, floor) {
  const f = Number(floor ?? DEFAULT_FLOORS[meta.key]);
  return meta.kind === 'rate'
    ? `≥ ${pct(f, (f * 100) % 1 ? 1 : 0)}`
    : `≤ ${pct(f, (f * 100) % 1 ? 1 : 0)}`;
}

/** Report → rows for the headline metrics table. */
export function metricRows(report, floors = DEFAULT_FLOORS) {
  const metrics = report?.metrics || {};
  return METRICS.map((meta) => {
    const m = metrics[meta.key] || null;
    const floor = m?.floor ?? floors?.[meta.key] ?? DEFAULT_FLOORS[meta.key];
    return {
      ...meta,
      m,
      floor,
      pass: metricPass(m, meta, { ...DEFAULT_FLOORS, ...floors }),
      text: metricText(m, meta),
    };
  });
}

/** { blocked, failing: [label], assessed } — sign-off below any floor needs an override. */
export function floorGate(report, floors) {
  if (!report) return { blocked: true, failing: [], assessed: 0, noReport: true };
  const rows = metricRows(report, floors);
  const failing = rows.filter((r) => r.pass === false).map((r) => r.label);
  return {
    blocked: failing.length > 0,
    failing,
    assessed: rows.filter((r) => r.pass !== null).length,
    noReport: false,
  };
}

export const MISS_CAUSES = {
  geofence_too_small: { label: 'Geofence too small', fix: 'Widen the site geofence' },
  device_gap: { label: 'Device gap', fix: 'Check the tracker on this truck' },
  registration_unresolved: { label: 'Unresolved registration', fix: 'Add a registration alias' },
  no_trip: { label: 'No trip found', fix: 'Check the trip plan for that day' },
  duplicate: { label: 'Duplicate in their records', fix: 'Remove the duplicate row' },
  other: { label: 'Other', fix: 'Investigate' },
};

export const missCauseLabel = (c) =>
  MISS_CAUSES[c]?.label || String(c || 'Unknown').replace(/_/g, ' ');

/** Misses grouped by cause, most common first: [{ cause, label, count }]. */
export function missSummary(misses) {
  const counts = new Map();
  (misses || []).forEach((m) =>
    counts.set(m.cause || 'other', (counts.get(m.cause || 'other') || 0) + 1)
  );
  return [...counts.entries()]
    .map(([cause, count]) => ({ cause, label: missCauseLabel(cause), count }))
    .sort((a, b) => b.count - a.count);
}

// ── Templates and CSV ───────────────────────────────────────────────────────

/** GET /parallel-runs/templates/:kind → [{ name, required, description, example }]. */
export function normaliseColumns(template) {
  const cols =
    template?.columns || template?.template?.columns || (Array.isArray(template) ? template : []);
  return cols
    .map((c) =>
      typeof c === 'string'
        ? { name: c, required: false, description: '', example: '' }
        : {
            name: c.name || c.column || c.key,
            required: !!c.required,
            description: c.description || c.help || '',
            example: c.example ?? '',
          }
    )
    .filter((c) => c.name);
}

const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const toCsv = (header, rows) =>
  [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';

/** Header row + one example row (when the template gives examples). */
export function templateCsv(columns) {
  const header = columns.map((c) => c.name);
  const example = columns.map((c) => c.example ?? '');
  return toCsv(header, example.some((x) => x !== '') ? [example] : []);
}

/** Manually entered rows (objects keyed by column) → CSV. */
export function rowsToCsv(columns, rows) {
  return toCsv(
    columns.map((c) => c.name),
    rows.map((r) => columns.map((c) => r[c.name] ?? ''))
  );
}

/** Import response → { imported, rejected, errors: [{ row, column, message }] }. */
export function readImportResult(res) {
  const errors = (res?.errors || res?.error_report || res?.rejected_rows || []).map((e) =>
    typeof e === 'string'
      ? { row: null, column: null, message: e }
      : {
          row: e.row ?? e.line ?? null,
          column: e.column ?? e.field ?? null,
          message: e.message || e.error || String(e),
        }
  );
  return {
    imported: Number(res?.imported ?? res?.inserted ?? res?.count ?? 0),
    rejected: Number(res?.rejected ?? errors.length),
    errors,
  };
}

export function errorReportCsv(errors) {
  return toCsv(
    ['row', 'column', 'problem'],
    (errors || []).map((e) => [e.row ?? '', e.column ?? '', e.message])
  );
}

// ── Daily comparison ────────────────────────────────────────────────────────

const dayOf = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // SAST calendar day (the operation's day), independent of the browser's zone.
  return new Date(d.getTime() + 2 * 3600000).toISOString().slice(0, 10);
};

/**
 * Per-day comparison: the server's `daily` series when given, else derived from
 * `records` (their trip sheets by match state). [{ date, theirs, matched, missed, extra, rate }]
 */
export function dailySeries(run) {
  if (Array.isArray(run?.daily) && run.daily.length) {
    return run.daily
      .map((d) => {
        const theirs = Number(d.theirs ?? (d.matched ?? 0) + (d.missed ?? 0));
        return {
          date: d.date,
          theirs,
          matched: Number(d.matched ?? 0),
          missed: Number(d.missed ?? 0),
          extra: Number(d.extra ?? 0),
          rate: theirs ? Number(d.matched ?? 0) / theirs : null,
        };
      })
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }
  const records = Array.isArray(run?.records)
    ? run.records.filter((r) => (r.kind || 'trip_sheet') === 'trip_sheet')
    : [];
  const map = new Map();
  records.forEach((r) => {
    const day = dayOf(r.at);
    if (!day) return;
    const d = map.get(day) || { date: day, theirs: 0, matched: 0, missed: 0, extra: 0 };
    if (r.match_state === 'extra') d.extra += 1;
    else {
      d.theirs += 1;
      if (r.match_state === 'matched') d.matched += 1;
      if (r.match_state === 'missed') d.missed += 1;
    }
    map.set(day, d);
  });
  return [...map.values()]
    .map((d) => ({ ...d, rate: d.theirs ? d.matched / d.theirs : null }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Record counts per kind from the run body ({ record_counts } or counted records). */
export function recordCounts(run) {
  if (run?.record_counts && typeof run.record_counts === 'object') return run.record_counts;
  const out = {};
  (run?.records || []).forEach((r) => (out[r.kind] = (out[r.kind] || 0) + 1));
  return out;
}

/** Index of the furthest step the run has reached (0-based). */
export function reachedStep(run, report) {
  if (!run) return 0;
  if (run.state === 'signed_off' || run.certificate_id) return 4;
  if (report || run.report) return 3;
  const counts = recordCounts(run);
  if (Object.values(counts).some((n) => Number(n) > 0)) return 2;
  return 1;
}

export const RUN_STATE_META = {
  open: { label: 'Running', glyph: '◔' },
  review: { label: 'In review', glyph: '◑' },
  signed_off: { label: 'Signed off', glyph: '●' },
};

export { pct as fmtRatio };
